import { useRoute, Link, useLocation } from "wouter";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Edit, MoreHorizontal, Mail, Phone, Loader2, Building2, Linkedin, Zap, Briefcase } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { EditCOIDialog } from "@/components/edit/EditCOIDialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { InlineEdit } from "@/components/InlineEdit";
import { useContactField } from "@/hooks/useContactField";
import { EntityRemindersButton } from "@/components/reminders/EntityRemindersButton";
import { useCurrentUserTimezone } from "@/hooks/useCurrentUserTimezone";
import { RailDocuments } from "@/components/record/RailDocuments";
import { OwnerPicker } from "@/components/OwnerPicker";
import { SuggestionRow } from "@/components/origination/SuggestionRow";
import { useSuggestions } from "@/hooks/useSuggestions";
import { CoiNetworkStrip } from "@/components/coi/CoiNetworkStrip";
import { CoiLinkedCompanies } from "@/components/coi/CoiLinkedCompanies";
import { CoiDiscoveredConnections } from "@/components/coi/CoiDiscoveredConnections";
import { ActivityTimeline } from "@/components/activity/ActivityTimeline";
import { CoiEnrichReviewDialog } from "@/components/coi/CoiEnrichReviewDialog";
import { CoiReferralsTable } from "@/components/coi/CoiReferralsTable";
import { CoiSentTable } from "@/components/coi/CoiSentTable";
import { CoiQuickActions } from "@/components/coi/CoiQuickActions";
import { SPECIALTY_LABELS } from "@/components/coi/specialty";
import { useCoiEnrichRun, useCoiEnrichApply } from "@/hooks/useCoiEnrich";
import { useCoiDetail } from "@/hooks/useCoiDetail";
import { useRecordCompose } from "@/components/record/useRecordCompose";
import { RecordFrame, RecordPanel } from "@/components/record/RecordFrame";
import { RecordHeader, RecordChip } from "@/components/record/RecordHeader";
import { ReachItem } from "@/components/record/ReachItem";
import { FactsStrip } from "@/components/record/FactsStrip";
import { ActionBar } from "@/components/record/ActionBar";
import { BriefPanel } from "@/components/record/BriefPanel";
import { RailPeople, type RailPerson } from "@/components/record/RailPeople";
import { RailFtcContact } from "@/components/record/RailFtcContact";
import { RailDetails } from "@/components/record/RailDetails";
import { RailNotes } from "@/components/record/RailNotes";
import { coiFacts } from "@/components/record/facts/personFacts";
import { useContactSummary } from "@/hooks/useRecordSummary";

const DELETE_COPY = "This removes the person from the CRM. Their logged activities and synced emails stay; referrals attributed to them keep their type and note but lose the name.";

export default function COIDetail() {
  const [, params] = useRoute("/coi/:id");
  const [, setLocation] = useLocation();
  const userTz = useCurrentUserTimezone();
  const queryClient = useQueryClient();
  const id = params?.id;
  const { data: coi, isLoading, update, notFound } = useCoiDetail(id);
  // The label decides the view: a contact who is not (or no longer) a COI gets the plain contact page.
  useEffect(() => { if (notFound && id) setLocation(`/contacts/${id}`, { replace: true }); }, [notFound, id]); // eslint-disable-line react-hooks/exhaustive-deps
  const { accept, dismiss } = useSuggestions({ contactId: id }, { list: false });
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [enrichOpen, setEnrichOpen] = useState(false);
  const [enrichPayload, setEnrichPayload] = useState<any>(null);
  const enrichRun = useCoiEnrichRun(id);
  const enrichApply = useCoiEnrichApply(id);
  const { saveFieldAsync: saveContactFieldAsync } = useContactField(coi?.id, undefined, [["coi", id]]);


  const { compose, setCompose, openLog, nextStepOpen, setNextStepOpen } = useRecordCompose();
  const summary = useContactSummary(coi?.id);
  const { data: firm } = useQuery<{ name: string; contacts?: RailPerson[] }>({
    queryKey: ["company", coi?.firmId],
    queryFn: async () => {
      const r = await fetch(`/api/companies/${coi?.firmId}`, { credentials: "include" });
      if (!r.ok) throw new Error("Failed to fetch company");
      return r.json();
    },
    enabled: !!coi?.firmId,
  });

  if (isLoading) return <AppLayout title="Loading..."><div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div></AppLayout>;
  if (!coi) return <AppLayout title="Not Found"><div className="text-center py-16"><p className="text-muted-foreground">COI not found.</p><Link href="/coi"><Button variant="outline" className="mt-4">Back to COI</Button></Link></div></AppLayout>;

  const first = coi.name.split(" ")[0];

  return (
    <AppLayout title={coi.name}>
      <RecordFrame
        header={
          <RecordHeader
            crumb={{ label: "COI", href: "/coi" }}
            current={coi.name}
            name={
              <InlineEdit label="Contact name"
                value={coi.name}
                onSave={async (v) => { const t = v.trim(); if (t && t !== coi.name) await saveContactFieldAsync("name", t); }}
                displayClassName="cursor-pointer rounded px-1 -mx-1 hover:bg-primary/5"
                inputClassName="w-full rounded border bg-background px-1 py-0.5 text-[22px] font-bold outline-none"
              />
            }
            chips={<>
              <RecordChip tone="category">COI</RecordChip>
              <RecordChip tone="gold">{SPECIALTY_LABELS[coi.specialty] ?? coi.specialty}</RecordChip>
            </>}
            subline={[
              { icon: <Briefcase />, content: <InlineEdit label="Job title" value={coi.title} placeholder="Add title" onSave={(v) => saveContactFieldAsync("title", v.trim() || null)} /> },
              ...(coi.firmName ? [{ icon: <Building2 />, content: coi.firmId ? <Link href={`/companies/${coi.firmId}`} className="hover:text-foreground hover:underline">{coi.firmName}</Link> : coi.firmName }] : []),
              ...(coi.geographicCoverage ? [{ content: coi.geographicCoverage }] : []),
              { content: <span className="inline-flex items-center gap-1.5">Owner <OwnerPicker value={coi.ownerUserId} onChange={(v) => update.mutate({ ownerUserId: v })} /></span> },
            ]}
            reach={<>
              {coi.email && <ReachItem icon={<Mail />} value={coi.email} />}
              {coi.phone && <ReachItem icon={<Phone />} value={coi.phone} />}
              {coi.linkedinUrl && <ReachItem icon={<Linkedin />} value="LinkedIn" href={coi.linkedinUrl} copy={false} />}
            </>}
            actions={
              <ActionBar onLog={openLog} email={coi.email} onNextStep={() => setNextStepOpen(true)}>
                <EntityRemindersButton entityType="contact" entityId={coi.id} entityName={coi.name} userTimezone={userTz} />
                <Button variant="outline" size="sm" className="gap-1.5" disabled={enrichRun.isPending}
                  onClick={() => enrichRun.mutate(undefined, { onSuccess: (d) => { setEnrichPayload(d); setEnrichOpen(true); }, onError: (e) => toast.error(String(e)) })}>
                  {enrichRun.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />} Enrich
                </Button>
                <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setEditOpen(true)}><Edit className="h-3.5 w-3.5" />Edit</Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild><Button variant="outline" size="sm" className="h-8 w-8 min-h-0 p-0" aria-label="More actions"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={async () => {
                    const r = await fetch(`/api/cois/${id}/unmark`, { method: "POST", credentials: "include" });
                    if (!r.ok) { toast.error("Failed to remove from COI"); return; }
                    queryClient.invalidateQueries({ queryKey: ["cois"] }); queryClient.invalidateQueries({ queryKey: ["coi-bd"] }); queryClient.invalidateQueries({ queryKey: ["coi-firm"] });
                    toast.success(`${coi.name} is a contact again, not a COI`); setLocation(`/contacts/${id}`);
                  }}>Remove from COI</DropdownMenuItem>
                  <DropdownMenuItem className="text-destructive" onSelect={(e) => { e.preventDefault(); setDeleteOpen(true); }}>Delete</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </ActionBar>
            }
          />
        }
        facts={<FactsStrip facts={coiFacts(coi, summary.data)} />}
        work={<>
          <BriefPanel
            summary={summary.data}
            loading={summary.isLoading}
            error={summary.isError}
            onRetry={() => summary.refetch()}
            openLabel={`Open with ${first}`}
            target={{ contactId: coi.id, companyId: coi.firmId ?? undefined }}
            nextStepOpen={nextStepOpen}
            onNextStepOpenChange={setNextStepOpen}
          />
          <RecordPanel title={`Referrals from ${first}`} count={coi.referrals.length}>
            <div className="mb-3"><CoiQuickActions coi={{ id: coi.id, name: coi.name, firmName: coi.firmName }} /></div>
            {coi.suggestions.length > 0 && (
              <div className="mb-3 space-y-2">
                {coi.suggestions.map((s) => (
                  <SuggestionRow key={s.id} s={s} pending={accept.isPending || dismiss.isPending}
                    onAccept={() => accept.mutate(s.id, { onSuccess: () => toast.success(s.direction === "in" ? "Attributed" : "Logged as sent") })}
                    onDismiss={() => dismiss.mutate(s.id)} />
                ))}
              </div>
            )}
            <CoiReferralsTable rows={coi.referrals} emptyText={`No referrals attributed to ${coi.name} yet. Use "New prospect from this COI" when they introduce someone.`} />
            <h4 className="mb-1.5 mt-4 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Sent to {first}</h4>
            <CoiSentTable rows={coi.sent} name={first} />
          </RecordPanel>
          <RecordPanel id="record-activity">
            <ActivityTimeline scope={{ kind: "contact", contactId: coi.id, contactName: coi.name }} compose={compose} onComposeChange={setCompose} />
          </RecordPanel>
          <RecordPanel title="Network">
            <div className="space-y-4">
              <CoiNetworkStrip coiId={coi.id} />
              <CoiLinkedCompanies coiId={coi.id} />
              <CoiDiscoveredConnections coiId={coi.id} />
            </div>
          </RecordPanel>
        </>}
        rail={<>
          <RailFtcContact rows={summary.data?.ftcContact} loading={summary.isLoading} error={summary.isError} />
          {coi.firmName && <RailPeople title={`At ${coi.firmName}`} people={(firm?.contacts ?? []).filter((p) => p.id !== coi.id)} />}
          <RailDetails rows={[
            ["Specialty", `${SPECIALTY_LABELS[coi.specialty] ?? coi.specialty}${coi.specialtyNotes ? ` · ${coi.specialtyNotes}` : ""}`],
            ["Coverage", coi.geographicCoverage ?? "Not set"],
            ...(coi.howWeMet ? [["How we met", coi.howWeMet] as [string, string]] : []),
            ["Added", new Date(coi.createdAt).toLocaleDateString("en-US", { month: "short", year: "numeric" })],
          ]}>
            {coi.firmBio && <p className="mt-3 whitespace-pre-wrap text-xs text-muted-foreground">{coi.firmBio}</p>}
          </RailDetails>
          <RailNotes value={coi.notes} onSave={async (v) => {
            try { await update.mutateAsync({ notes: v }); }
            catch (e) { toast.error("Couldn't save the notes"); throw e; }
          }} />
          <RailDocuments contactId={coi.id} />
        </>}
      />

      <CoiEnrichReviewDialog open={enrichOpen} onOpenChange={setEnrichOpen} payload={enrichPayload} applying={enrichApply.isPending}
        onApply={(fields) => enrichApply.mutate(fields, { onSuccess: () => { setEnrichOpen(false); toast.success("Applied"); } })} />
      <EditCOIDialog open={editOpen} onOpenChange={setEditOpen} coi={coi} />
      <ConfirmDialog open={deleteOpen} onOpenChange={(n) => !n && !isDeleting && setDeleteOpen(n)} title={`Delete ${coi.name}?`} description={DELETE_COPY}
        confirmLabel="Delete COI" cancelLabel="Cancel" variant="destructive" isLoading={isDeleting}
        onConfirm={async () => {
          if (isDeleting) return;
          setIsDeleting(true);
          try {
            const r = await fetch(`/api/cois/${id}`, { method: "DELETE", credentials: "include" });
            if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? "Failed to delete");
            queryClient.invalidateQueries({ queryKey: ["cois"] }); queryClient.invalidateQueries({ queryKey: ["coi-bd"] });
            toast.success(`Deleted ${coi.name}`); setDeleteOpen(false); setLocation("/coi");
          } catch (e) { toast.error(e instanceof Error ? e.message : "Failed to delete"); } finally { setIsDeleting(false); }
        }} />
    </AppLayout>
  );
}
